import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type {
  MyCandidateFromAPI,
  CandidateStatusEnum,
} from "../../types/candidate";
import { CandidateStatusEnum as CandidateStatusValues } from "../../types/candidate";
import { cn, toTitleCase, jobTitleFromJdPath } from "../../lib/utils";
import { getScoreColor, employmentTypeLabel, yearsExpLabel } from "./candidate-utils";
import { useCandidateData } from "../../hooks/useCandidateData";
import { useUpdateCandidateStatus } from "../../hooks/useUpdateCandidateStatus";
import { ApiClientError } from "../../lib/api-client";
import { env } from "../../lib/env";
import { CandidateDetailModal } from "./CandidateDetailModal";
import { GreetingPreviewModal } from "./GreetingPreviewModal";
import { CandidateStatusDropdown } from "./CandidateStatusDropdown";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { IconButton } from "../ui/IconButton";
import { EmptyState } from "../ui/EmptyState";
import { Skeleton } from "../ui/Skeleton";
import UsersGroupTwoRoundedLinear from "~icons/solar/users-group-two-rounded-linear";
import LetterLinear from "~icons/solar/letter-linear";
import ArrowRightLinear from "~icons/solar/arrow-right-linear";
import AltArrowLeftLinear from "~icons/solar/alt-arrow-left-linear";
import AltArrowRightLinear from "~icons/solar/alt-arrow-right-linear";
import DangerCircleLinear from "~icons/solar/danger-circle-linear";
import CloseCircleLinear from "~icons/solar/close-circle-linear";
import CheckCircleLinear from "~icons/solar/check-circle-linear";

/** v3 itemsPerPage */
const PAGE_SIZE = 8;
const SHOW_SCORE = env.FEATURE_CONFIDENCE_SCORE_UI;

interface CandidateListModalProps {
  jdPath: string;
  onClose: () => void;
  /** Open straight onto this candidate's profile (inline card click). */
  initialCandidate?: MyCandidateFromAPI | null;
}

type NoticeTone = "green" | "red";

// ---------------------------------------------------------------------------
// Shared bits (also used by CandidateDetailModal)
// ---------------------------------------------------------------------------

/** v3 close control: 32px round ghost button with a plain X. */
export function ModalCloseButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title="Close modal"
      aria-label="Close"
      className="flex size-8 items-center justify-center rounded-full text-ink-muted transition hover:bg-black/5 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
    >
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="size-5"
        aria-hidden="true"
      >
        <path d="M18 6L6 18M6 6l12 12" />
      </svg>
    </button>
  );
}

/** Page list with an ellipsis window once there are many pages. */
function pageItems(current: number, total: number): Array<number | "…"> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = new Set<number>([1, total, current - 1, current, current + 1]);
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out: Array<number | "…"> = [];
  sorted.forEach((p, i) => {
    if (i > 0 && p - sorted[i - 1] > 1) out.push("…");
    out.push(p);
  });
  return out;
}

// ---------------------------------------------------------------------------
// Notice — inline status strip; body-level toasts are inert under showModal()
// ---------------------------------------------------------------------------

function Notice({
  tone,
  children,
  onDismiss,
}: {
  tone: NoticeTone;
  children: string;
  onDismiss: () => void;
}) {
  const Icon = tone === "green" ? CheckCircleLinear : DangerCircleLinear;
  return (
    <div
      role="status"
      className={cn(
        "flex items-center gap-2 rounded-2xl border px-4 py-2.5 text-xs font-medium motion-safe:animate-fade-in",
        tone === "green"
          ? "border-status-green-border bg-status-green-bg text-status-green-text"
          : "border-error/20 bg-error-light text-error"
      )}
    >
      <Icon aria-hidden="true" className="size-4 shrink-0" />
      <span className="min-w-0 flex-1">{children}</span>
      <IconButton
        icon={CloseCircleLinear}
        aria-label="Dismiss"
        size="sm"
        onClick={onDismiss}
        className="-mr-1 text-current hover:bg-black/[0.05] hover:text-current"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

function SkeletonRow({ cells }: { cells: number }) {
  return (
    <tr>
      <td className="px-6 py-3.5">
        <div className="flex items-center gap-3">
          <Skeleton className="size-10 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-3.5 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      </td>
      {Array.from({ length: cells - 1 }).map((_, i) => (
        <td key={i} className="px-4 py-3.5">
          <Skeleton className="mx-auto h-8 w-24 rounded-full" />
        </td>
      ))}
    </tr>
  );
}

interface CandidateRowProps {
  candidate: MyCandidateFromAPI;
  onOpen: () => void;
  onStatusChange: (status: CandidateStatusEnum) => void;
  onEmail: () => void;
}

function CandidateRow({ candidate, onOpen, onStatusChange, onEmail }: CandidateRowProps) {
  const name = toTitleCase(candidate.full_name);
  const score = candidate.confidence_score?.overall_score;
  const canContact =
    candidate.status === CandidateStatusValues.NEW ||
    candidate.status === CandidateStatusValues.CONTACTED;
  const canEmail = Boolean(candidate.email) && canContact;
  const linkedin = candidate.linkedin_profile_url;
  // v3: "location • worktype" under the name; email if neither is known
  const subline =
    [candidate.location, employmentTypeLabel(candidate.employment_type)]
      .filter(Boolean)
      .join(" • ") || candidate.email || "No email on file";
  const role = candidate.current_job_title ? toTitleCase(candidate.current_job_title) : null;
  const experience = yearsExpLabel(candidate.years_of_experience);

  return (
    <tr className="group/row bg-white transition-colors duration-150 hover:bg-surface-2">
      {/* Column 1: Candidate (avatar + name, location • worktype below) */}
      <td className="px-6 py-3.5">
        <div className="flex items-center gap-3">
          <div className="relative size-10 shrink-0 overflow-hidden rounded-full border border-black/10 bg-surface-3 shadow-xs">
            {candidate.avatar_url ? (
              <img src={candidate.avatar_url} alt="" className="size-full object-cover" />
            ) : (
              <div className="flex size-full items-center justify-center text-xs font-bold text-ink-muted">
                {name.slice(0, 2).toUpperCase()}
              </div>
            )}
          </div>
          <div className="min-w-0">
            <button
              type="button"
              onClick={onOpen}
              className="block max-w-full cursor-pointer truncate text-left text-sm font-semibold leading-tight text-ink transition-colors group-hover/row:text-primary focus-visible:outline-none focus-visible:text-primary"
            >
              {name}
            </button>
            <p className="mt-1 truncate text-xs text-ink-muted">{subline}</p>
          </div>
        </div>
      </td>

      {/* Column 2: Role & Experience */}
      <td className="px-6 py-3.5">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold leading-tight text-ink" title={role ?? undefined}>
            {role ?? <span className="font-normal text-ink-faint">—</span>}
          </p>
          <p className="mt-1 truncate text-xs text-ink-muted">
            {experience ?? (candidate.current_company ? toTitleCase(candidate.current_company) : "")}
          </p>
        </div>
      </td>

      {/* Column 3: Match score (feature-flagged) */}
      {SHOW_SCORE && (
        <td className="px-6 py-3.5">
          {score != null ? (
            <span className={cn("text-base font-bold tabular-nums", getScoreColor(score))}>
              {Math.round(score)}%
            </span>
          ) : (
            <span className="text-sm text-ink-faint">—</span>
          )}
        </td>
      )}

      {/* Column 3: Status */}
      <td className="px-4 py-3.5 text-center">
        <div className="flex justify-center">
          <CandidateStatusDropdown value={candidate.status} onChange={onStatusChange} />
        </div>
      </td>

      {/* Column 4: Contact channels */}
      <td className="px-4 py-3.5 text-center">
        <div className="flex items-center justify-center gap-2.5">
          <button
            type="button"
            onClick={onEmail}
            disabled={!canEmail}
            title={
              !canContact
                ? "Contact not available for this candidate's current status"
                : candidate.email
                  ? `Email ${name}`
                  : "No email available"
            }
            aria-label={`Email ${name}`}
            className="flex size-9 items-center justify-center rounded-full border border-black/10 bg-white text-ink-muted shadow-2xs transition hover:scale-105 hover:border-primary/40 hover:bg-primary-light hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:scale-100 disabled:hover:border-black/10 disabled:hover:bg-white disabled:hover:text-ink-muted"
          >
            <LetterLinear aria-hidden="true" className="size-[17px]" />
          </button>
          {linkedin ? (
            <a
              href={linkedin}
              target="_blank"
              rel="noreferrer"
              title="LinkedIn Profile"
              aria-label={`${name} on LinkedIn`}
              className="flex size-9 items-center justify-center rounded-full bg-linkedin/10 text-linkedin shadow-2xs transition hover:scale-105 hover:bg-linkedin hover:text-white"
            >
              <span className="text-[13px] font-bold leading-none">in</span>
            </a>
          ) : (
            <span
              title="No LinkedIn profile on file"
              className="flex size-9 items-center justify-center rounded-full bg-linkedin/10 text-linkedin opacity-40"
            >
              <span className="text-[13px] font-bold leading-none">in</span>
            </span>
          )}
        </div>
      </td>

      {/* Column 5: Profile action */}
      <td className="px-6 py-3.5 text-right">
        <button
          type="button"
          onClick={onOpen}
          className="group/btn inline-flex h-9 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full border border-black/15 bg-white px-5 text-xs font-semibold text-ink shadow-xs transition-all duration-150 hover:border-primary hover:bg-primary hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <span>Profile</span>
          <ArrowRightLinear
            aria-hidden="true"
            className="size-3.5 text-ink-muted transition-colors group-hover/btn:text-white"
          />
        </button>
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// CandidateListModal
// ---------------------------------------------------------------------------

/**
 * Candidates matched to a chat result (v3 CandidatesModal, list view).
 * Mounted only while open, so the Modal is always `open` and closing
 * unmounts it. The detail and email modals render as siblings so each gets
 * its own top-layer entry above this one.
 */
export default function CandidateListModal({
  jdPath,
  onClose,
  initialCandidate,
}: CandidateListModalProps) {
  const {
    candidates,
    total,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    refetch,
  } = useCandidateData(jdPath);

  const { updateStatus, isUpdating } = useUpdateCandidateStatus();
  const queryClient = useQueryClient();
  const jobTitle = useMemo(() => jobTitleFromJdPath(jdPath), [jdPath]);

  const [currentPage, setCurrentPage] = useState(1);
  const [updatingCandidateId, setUpdatingCandidateId] = useState<string | null>(null);
  const [detailCandidate, setDetailCandidate] = useState<MyCandidateFromAPI | null>(
    initialCandidate ?? null
  );
  const [greetingTarget, setGreetingTarget] = useState<MyCandidateFromAPI | null>(null);

  // Notice state
  const [notice, setNotice] = useState<{ message: string; tone: NoticeTone } | null>(null);
  const noticeTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const showNotice = useCallback((message: string, tone: NoticeTone) => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    setNotice({ message, tone });
    noticeTimerRef.current = setTimeout(() => setNotice(null), 3000);
  }, []);

  const dismissNotice = useCallback(() => {
    if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    setNotice(null);
  }, []);

  useEffect(() => {
    return () => {
      if (noticeTimerRef.current) clearTimeout(noticeTimerRef.current);
    };
  }, []);

  // ----- Pagination (8 per page over the server pages already fetched) -----
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageRows = candidates.slice(pageStart, pageStart + PAGE_SIZE);
  const expectedOnPage = Math.max(0, Math.min(PAGE_SIZE, total - pageStart));
  const missingOnPage = Math.max(0, expectedOnPage - pageRows.length);

  // Pull the next server page whenever the visible page isn't fully loaded yet.
  useEffect(() => {
    if (missingOnPage > 0 && hasNextPage && !isFetchingNextPage) fetchNextPage();
  }, [missingOnPage, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // The hook handles optimistic updates on the project candidates cache.
  // We still need to invalidate the my-candidates list so this modal refreshes.
  const handleStatusChange = useCallback(
    async (
      candidateStatusId: string,
      projectId: string,
      newStatus: CandidateStatusEnum,
      candidateId: string
    ) => {
      setUpdatingCandidateId(candidateId);
      try {
        await updateStatus({
          id: candidateStatusId,
          status: newStatus,
          projectId,
          candidateId,
        });
        void queryClient.invalidateQueries({
          queryKey: ["candidates", "my-candidates"],
        });
        showNotice("Candidate status updated.", "green");
      } catch (err) {
        console.error("Failed to update status:", err);
        const message =
          err instanceof ApiClientError && err.errors.length > 0
            ? err.errors[0]
            : "Failed to update status. Please try again.";
        showNotice(message, "red");
      } finally {
        setUpdatingCandidateId(null);
      }
    },
    [updateStatus, queryClient, showNotice]
  );

  const columnCount = SHOW_SCORE ? 6 : 5;

  const renderBody = () => {
    if (isLoading) {
      return (
        <tbody className="divide-y divide-black/[0.05]">
          {Array.from({ length: PAGE_SIZE }).map((_, i) => (
            <SkeletonRow key={i} cells={columnCount} />
          ))}
        </tbody>
      );
    }
    if (error) {
      return (
        <tbody>
          <tr>
            <td colSpan={columnCount} className="py-10">
              <EmptyState
                compact
                icon={DangerCircleLinear}
                title="Couldn't load candidates"
                description={error}
                action={
                  <Button variant="secondary" size="sm" onClick={refetch}>
                    Retry
                  </Button>
                }
              />
            </td>
          </tr>
        </tbody>
      );
    }
    if (total === 0) {
      return (
        <tbody>
          <tr>
            <td colSpan={columnCount} className="py-10">
              <EmptyState
                compact
                icon={UsersGroupTwoRoundedLinear}
                title="No candidates yet"
                description="Nothing matched this search so far."
              />
            </td>
          </tr>
        </tbody>
      );
    }
    return (
      <tbody className="divide-y divide-black/[0.05] text-sm text-ink">
        {pageRows.map((candidate) => (
          <CandidateRow
            key={candidate.candidate_id}
            candidate={candidate}
            onOpen={() => setDetailCandidate(candidate)}
            onEmail={() => setGreetingTarget(candidate)}
            onStatusChange={(newStatus) => {
              if (!candidate.id) return;
              void handleStatusChange(
                candidate.id,
                candidate.project_id,
                newStatus,
                candidate.candidate_id
              );
            }}
          />
        ))}
        {Array.from({ length: missingOnPage }).map((_, i) => (
          <SkeletonRow key={`pending-${i}`} cells={columnCount} />
        ))}
      </tbody>
    );
  };

  const showingFrom = total === 0 ? 0 : pageStart + 1;
  const showingTo = Math.min(currentPage * PAGE_SIZE, total);

  return (
    <>
      <Modal
        open
        onClose={onClose}
        title="Candidates List"
        size="2xl"
        flush
        hideHeader
      >
        <div className="flex flex-col">
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between border-b border-black/[0.06] bg-white px-6 py-4">
            <div className="flex items-center gap-3">
              <div className="flex size-9 items-center justify-center rounded-xl bg-primary-light text-primary">
                <UsersGroupTwoRoundedLinear aria-hidden="true" className="size-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-lg font-bold text-ink">Candidates List</h2>
                  <span className="rounded-full bg-surface-3 px-2.5 py-0.5 text-xs font-semibold text-ink-muted">
                    {isLoading ? "…" : `${total} ${total === 1 ? "candidate" : "candidates"}`}
                  </span>
                </div>
                <p className="text-xs text-ink-muted">{jobTitle}</p>
              </div>
            </div>
            <ModalCloseButton onClick={onClose} />
          </div>

          {notice && (
            <div className="px-6 pt-4">
              <Notice tone={notice.tone} onDismiss={dismissNotice}>
                {notice.message}
              </Notice>
            </div>
          )}

          {/* Table */}
          <div className="no-scrollbar max-h-[calc(92vh-122px)] flex-1 overflow-y-auto">
            <table className="w-full table-fixed border-collapse text-left">
              <colgroup>
                <col className="w-[30%]" />
                <col className={SHOW_SCORE ? "w-[22%]" : "w-[30%]"} />
                {SHOW_SCORE && <col className="w-[10%]" />}
                <col className="w-[16%]" />
                <col className="w-[12%]" />
                <col className="w-[12%]" />
              </colgroup>
              <thead className="sticky top-0 z-10">
                <tr className="whitespace-nowrap border-b border-black/[0.06] bg-surface-2 text-[13px] font-semibold text-ink-muted">
                  <th className="whitespace-nowrap px-6 py-3.5">Candidate</th>
                  <th className="whitespace-nowrap px-6 py-3.5">Role &amp; Experience</th>
                  {SHOW_SCORE && <th className="whitespace-nowrap px-6 py-3.5">Match</th>}
                  <th className="whitespace-nowrap px-4 py-3.5 text-center">Current Status</th>
                  <th className="whitespace-nowrap px-4 py-3.5 text-center">Contact</th>
                  <th className="whitespace-nowrap px-6 py-3.5 text-right">Action</th>
                </tr>
              </thead>
              {renderBody()}
            </table>
          </div>

          {/* Footer with pagination */}
          <div className="flex shrink-0 items-center justify-between border-t border-black/[0.06] bg-surface-2 px-6 py-3">
            <p className="text-xs text-ink-muted">
              Showing{" "}
              <span className="font-semibold text-ink">
                {showingFrom}–{showingTo}
              </span>{" "}
              from <span className="font-semibold text-ink">{total}</span> candidates
            </p>

            <nav className="flex items-center gap-1" aria-label="Pagination">
              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                aria-label="Previous page"
                className="flex size-7 items-center justify-center rounded-full border border-black/10 text-ink-muted transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-30"
              >
                <AltArrowLeftLinear aria-hidden="true" className="size-3.5" />
              </button>

              {pageItems(currentPage, totalPages).map((pg, i) =>
                pg === "…" ? (
                  <span key={`gap-${i}`} className="px-1 text-xs text-ink-faint">
                    …
                  </span>
                ) : (
                  <button
                    key={pg}
                    type="button"
                    onClick={() => setCurrentPage(pg)}
                    aria-current={currentPage === pg ? "page" : undefined}
                    className={cn(
                      "flex size-7 items-center justify-center rounded-full text-xs font-semibold transition",
                      currentPage === pg
                        ? "bg-ink text-white shadow-xs"
                        : "text-ink-muted hover:bg-black/5 hover:text-ink"
                    )}
                  >
                    {pg}
                  </button>
                )
              )}

              <button
                type="button"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                aria-label="Next page"
                className="flex size-7 items-center justify-center rounded-full border border-black/10 text-ink-muted transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-30"
              >
                <AltArrowRightLinear aria-hidden="true" className="size-3.5" />
              </button>
            </nav>
          </div>
        </div>
      </Modal>

      {/* Siblings, not children: each <dialog> needs its own top-layer entry */}
      {detailCandidate && (
        <CandidateDetailModal
          candidate={detailCandidate}
          jdPath={jdPath}
          jobTitle={jobTitle}
          onClose={() => setDetailCandidate(null)}
          onStatusChange={
            detailCandidate.id
              ? (newStatus) => {
                  void handleStatusChange(
                    detailCandidate.id!,
                    detailCandidate.project_id,
                    newStatus,
                    detailCandidate.candidate_id
                  );
                }
              : undefined
          }
          isStatusUpdating={
            isUpdating && updatingCandidateId === detailCandidate.candidate_id
          }
        />
      )}

      <GreetingPreviewModal
        open={greetingTarget !== null}
        onClose={() => setGreetingTarget(null)}
        candidateId={greetingTarget?.candidate_id ?? ""}
        candidateName={greetingTarget ? toTitleCase(greetingTarget.full_name) : ""}
        candidateEmail={greetingTarget?.email}
        projectId={greetingTarget?.project_id ?? ""}
        onSuccess={(msg) => {
          setGreetingTarget(null);
          showNotice(msg, "green");
          refetch();
        }}
        onError={(msg) => showNotice(msg, "red")}
      />
    </>
  );
}
